import React from "react"
import ReactDOM from "react-dom"

// Styles
import useStyles from "./styles"

const GAP = 8;
const EDGE = 8;
const ARROW_INSET = 14;

let sequence = 0;

type Anchor = {left: number, top: number, right: number, bottom: number, width: number}

type Coords = {left: number, top: number, arrowLeft: number, above: boolean}

type ModernTooltipProps = {
    text: string | null | undefined
    className?: string
    children: React.ReactNode
}
export default function ModernTooltip(props: ModernTooltipProps) {
    const styles = useStyles();
    const {text, className, children} = props;

    const tipRef = React.useRef<HTMLDivElement>(null);
    const triggerRef = React.useRef<HTMLSpanElement>(null);
    const [anchor, setAnchor] = React.useState<Anchor | null>(null);
    const [coords, setCoords] = React.useState<Coords | null>(null);
    const [tipId] = React.useState(() => `modern-tip-${++sequence}`);

    const body = (text || "").trim();
    const open = body.length > 0 && anchor !== null;

    const show = React.useCallback((e: React.SyntheticEvent) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();

        setAnchor({
            left: rect.left,
            top: rect.top,
            right: rect.right,
            bottom: rect.bottom,
            width: rect.width,
        })
    }, []);

    const hide = React.useCallback(() => {
        setAnchor(null);
        setCoords(null)
    }, []);

    React.useLayoutEffect(() => {
        const node = tipRef.current;

        if (false === open || node === null) {
            return
        }

        const box = node.getBoundingClientRect();
        const anchored = anchor as Anchor;
        const centre = anchored.left + anchored.width / 2;

        const rawLeft = centre - box.width / 2;
        const left = Math.max(EDGE, Math.min(rawLeft, window.innerWidth - box.width - EDGE));
        const fitsBelow = anchored.bottom + GAP + box.height <= window.innerHeight - EDGE;
        const fitsAbove = anchored.top - GAP - box.height >= EDGE;
        const above = false === fitsBelow && fitsAbove;

        setCoords({
            left: left,
            top: above ? anchored.top - GAP - box.height : anchored.bottom + GAP,
            arrowLeft: Math.max(
              ARROW_INSET,
              Math.min(centre - left, box.width - ARROW_INSET),
            ),
            above: above,
        })
    }, [open, anchor]);

    React.useEffect(() => {
        if (false === open) {
            return () => undefined
        }
        const closeOnOutsideTouch = (e: Event) => {
            const trigger = triggerRef.current;
            const target = e.target;

            if (trigger !== null && target instanceof Node && trigger.contains(target)) {
                return
            }

            hide()
        };

        window.addEventListener("scroll", hide, true);
        window.addEventListener("resize", hide);
        window.addEventListener("orientationchange", hide);
        document.addEventListener("touchstart", closeOnOutsideTouch, true);

        return () => {
            window.removeEventListener("scroll", hide, true);
            window.removeEventListener("resize", hide);
            window.removeEventListener("orientationchange", hide);
            document.removeEventListener("touchstart", closeOnOutsideTouch, true)
        }
    }, [open, hide]);

    if (body.length === 0) {
        return <React.Fragment>{children}</React.Fragment>
    }

    return (
      <React.Fragment>
          <span
            ref={triggerRef}
            className={`${styles.trigger} ${className || ""}`}
            tabIndex={0}
            aria-describedby={open ? tipId : undefined}
            onMouseEnter={show}
            onFocus={show}
            onTouchStart={show}
            onMouseLeave={hide}
            onBlur={hide}
            onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Escape" && open) {
                    e.stopPropagation();
                    hide()
                }
            }}
          >
              {children}
          </span>

          {open && ReactDOM.createPortal(
            <div
              ref={tipRef}
              id={tipId}
              role="tooltip"
              className={`${styles.tip} ${coords === null ? styles.tip__measuring : ""}`}
              style={coords === null ? undefined : {left: coords.left, top: coords.top}}
            >
                {body}
                {coords !== null && (
                  <i
                    className={`${styles.arrow} ${coords.above ? styles.arrow__above : styles.arrow__below}`}
                    style={{left: coords.arrowLeft - 4}}
                  />
                )}
            </div>,
            document.body,
          )}
      </React.Fragment>
    )
}
